<script setup lang='ts'>
import { KsExpand } from '@aschehoug/kloss'

withDefaults(defineProps<{
  variant?: AccordionVariant
  size?: AccordionSize
  disabled?: boolean
  divider?: boolean
}>(), {
  variant: 'standard',
  size: 'small',
  disabled: false,
  divider: true,
})
</script>

<template>
  <ClientOnly>
    <KsExpand
      :size="size"
      :disabled="disabled"
      :class="[`ks-expand-${variant}`, { 'ks-expand-no-divider': !divider }]"
    >
      <slot />
    </KsExpand>
  </ClientOnly>
</template>

<style scoped lang='postcss'>
.ks-expand {
  @apply gap-m;

  &.ks-expand-small {
    @apply text-base font-light;

    :deep(.ks-expand-toggle) {
      .ks-expand-button {
        @apply px-s py-xs !important;
      }
    }
  }

  &.ks-expand-medium {
    @apply text-lg font-light;

    :deep(.ks-expand-toggle) {
      .ks-expand-button {
        @apply px-s py-s !important;
      }
    }
  }

  &.ks-expand-large {
    @apply font-heading text-2xl sm:text-4xl font-light;

    :deep(.ks-expand-toggle) {
      .ks-expand-button {
        @apply px-s py-m !important;
      }
    }
  }

  &.ks-expand-standard {
    :deep(.ks-expand-toggle) {
      @apply hover:rounded-lg hover:bg-blurple-100 dark:hover:bg-transparent outline focus:outline-blurple-300 focus:outline focus:outline-2 focus:border-transparent !important;

      &:has(> .ks-expand-button[aria-expanded='false']) {
        @apply border-b border-blurple-500 hover:border-transparent !important;
      }

      &:has(> .ks-expand-button[aria-expanded='true']) {
        @apply border-b border-transparent !important;
      }

      &:has(> .ks-expand-button[disabled]) {
        @apply bg-transparent border-b border-blurple-500 hover:border-blurple-500 hover:rounded-none !important;
      }

      .ks-expand-button {
        @apply bg-transparent dark:text-carbon-200 active:text-blurple-500 justify-start !important;

        > svg {
          @apply w-5 h-5 !important;
        }
      }

      .ks-expand-button[aria-expanded='true'] {
        @apply bg-white border-white rounded-none rounded-t-lg active:text-blurple-500 hover:bg-transparent justify-start !important;
      }

      .ks-expand-button[disabled] {
        @apply text-carbon-300 !important;
      }
    }

    :deep(.ks-expand-content) {
      @apply bg-transparent border-b border-blurple-500 !important;

      > div {
        @apply p-0 !important;
      }
    }
  }

  &.ks-expand-standard.ks-expand-no-divider {
    :deep(.ks-expand-toggle) {
      &,
      &:has(> .ks-expand-button[aria-expanded='false']),
      &:has(> .ks-expand-button[aria-expanded='true']),
      &:has(> .ks-expand-button[disabled]) {
        @apply border-b-0 !important;
      }
    }

    :deep(.ks-expand-content) {
      @apply border-b-0 !important;
    }
  }

  &.ks-expand-highlighted {
    :deep(.ks-expand-toggle) {
      .ks-expand-button {
        @apply bg-carbon-200 rounded-lg active:text-blurple-500 hover:bg-blurple-100 focus:bg-blurple-100 justify-start focus:outline-blurple-300 !important;

        > svg {
          @apply w-5 h-5 !important;
        }
      }

      .ks-expand-button[aria-expanded='true'] {
        @apply bg-carbon-100 rounded-none rounded-t-lg active:text-blurple-500 hover:bg-blurple-100 justify-start !important;
      }
    }

    :deep(.ks-expand-content) {
      @apply bg-carbon-100 rounded-b-lg !important;

      > div {
        @apply p-0 rounded-none !important;
      }
    }
  }
}
</style>
